import React from "react";
import { MapPin } from "lucide-react";
import googleIcon from "@images/icons/social/google.svg";

// Our own Google rating badge (replaces the Trustindex widgets in the
// service-page hero and the desktop footer). No third-party script: the
// whole badge links to our real Google Business Profile on Google Maps.
// GOOGLE_RATING / GOOGLE_REVIEW_COUNT are the live values on that listing
// (checked 2026-10-19: 5.0 from 15 reviews). Update them here if they change.
export const GOOGLE_MAPS_URL = "https://www.google.com/maps/place/?q=place_id:ChIJqSDNva_jLj4ReVZpRcBpCVw";
export const GOOGLE_PLACE_ID = "ChIJqSDNva_jLj4ReVZpRcBpCVw";
export const GOOGLE_RATING = "5.0";
export const GOOGLE_REVIEW_COUNT = 15;

// Scoped CSS, not Tailwind: the pinned live app CSS lacks new utility classes.
const CSS = `
.wz-gb{text-decoration:none;color:#111827;-webkit-tap-highlight-color:transparent}
.wz-gb-stars{display:inline-flex;align-items:center;gap:1px;direction:ltr}
.wz-gb-stars svg{display:block;fill:#F5B301}
.wz-gb-pill{display:inline-flex;align-items:center;justify-content:center;padding-block:4px;max-width:100%}
.wz-gb-pill-in{display:inline-flex;align-items:center;gap:6px;height:36px;max-width:100%;padding:0 12px;box-sizing:border-box;background:#fff;border:1px solid #e5e7eb;border-radius:999px;box-shadow:0 2px 8px rgba(15,23,42,.06);white-space:nowrap;overflow:hidden;transition:border-color .2s}
.wz-gb-pill:hover .wz-gb-pill-in{border-color:#93c5fd}
.wz-gb-pill-in>img{width:15px;height:15px;flex:0 0 15px}
.wz-gb-pill-title{font-size:11px;font-weight:700;line-height:1}
.wz-gb-pill-score{font-size:11px;font-weight:800;line-height:1}
.wz-gb-pill-map{display:inline-flex;align-items:center;gap:3px;padding-inline-start:8px;border-inline-start:1px solid #e5e7eb;font-size:10px;font-weight:600;line-height:1;color:#4b5563}
.wz-gb-pill-map svg{width:11px;height:11px;color:#2563eb}
@media (max-width:420px){.wz-gb-pill-map>span{display:none}}
.wz-gb-card{display:flex;flex-direction:column;width:100%;max-width:240px;background:#fff;border:1px solid #d1d5db;border-radius:16px;overflow:hidden;text-align:center;transition:transform .2s,box-shadow .2s}
.wz-gb-card:hover{transform:translateY(-2px);box-shadow:0 10px 24px rgba(0,0,0,.25)}
.wz-gb-card-title{padding:14px 12px;font-size:15px;font-weight:800;line-height:1.5}
.wz-gb-card-body{display:flex;flex-direction:column;align-items:center;gap:6px;padding:14px 12px;border-block:1px solid #e5e7eb}
.wz-gb-card-body>img{width:40px;height:40px}
.wz-gb-card-score{font-size:26px;font-weight:800;line-height:1}
.wz-gb-card-foot{display:flex;align-items:center;justify-content:center;gap:4px;padding:10px 12px;font-size:12px;font-weight:600;color:#374151;white-space:nowrap}
.wz-gb-card-foot svg{width:14px;height:14px;flex:0 0 14px;color:#2563eb}
`;

const Stars = ({ size }) => (
    <span className="wz-gb-stars" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => (
            <svg key={i} width={size} height={size} viewBox="0 0 24 24">
                <path d="M12 2.5l2.94 6.06 6.56.92-4.78 4.6 1.17 6.52L12 17.5l-5.89 3.1 1.17-6.52-4.78-4.6 6.56-.92z" />
            </svg>
        ))}
    </span>
);

const GoogleRatingBadge = ({ variant = "pill", className = "" }) => {
    const year = new Date().getFullYear();
    const title = `الخدمة الأعلى تقييمًا لعام ${year}`;
    const linkProps = {
        href: GOOGLE_MAPS_URL,
        target: "_blank",
        rel: "noopener noreferrer",
        "aria-label": `${title}: تقييم ${GOOGLE_RATING} من 5 على Google — افتح موقعنا على خرائط Google`,
    };

    if (variant === "card") {
        return (
            <>
                <style>{CSS}</style>
                <a {...linkProps} dir="rtl" className={`wz-gb wz-gb-card ${className}`}>
                    <span className="wz-gb-card-title">
                        الخدمة
                        <br />
                        الأعلى تقييمًا لعام {year}
                    </span>
                    <span className="wz-gb-card-body">
                        <img src={googleIcon} alt="" width="40" height="40" loading="lazy" />
                        <span className="wz-gb-card-score">{GOOGLE_RATING}</span>
                        <Stars size={22} />
                    </span>
                    <span className="wz-gb-card-foot">
                        <MapPin aria-hidden="true" />
                        تقييماتنا على خرائط Google
                    </span>
                </a>
            </>
        );
    }

    return (
        <>
            <style>{CSS}</style>
            <a {...linkProps} dir="rtl" className={`wz-gb wz-gb-pill ${className}`}>
                <span className="wz-gb-pill-in">
                    <img src={googleIcon} alt="" width="15" height="15" />
                    <span className="wz-gb-pill-title">{title}</span>
                    <Stars size={11} />
                    <span className="wz-gb-pill-score">{GOOGLE_RATING}</span>
                    <span className="wz-gb-pill-map">
                        <MapPin aria-hidden="true" />
                        <span>خرائط Google</span>
                    </span>
                </span>
            </a>
        </>
    );
};

export default GoogleRatingBadge;
